import React from 'react';
import ManualBookShell from './manual/ManualBookShell';

const WORKFLOW_ID = 'ORbKYOC7jl0GBSkt';
const WORKFLOW_HOST = 'n8n.farmagsa.com.ar';

const pipelineSteps = [
  {
    title: 'Entrada Chatwoot / Webhook',
    body: 'El mensaje del paciente entra por webhook de Chatwoot. Se normaliza el payload, se identifica conversationId y se prepara el contexto base para el resto del workflow.'
  },
  {
    title: 'Contexto cliente',
    body: 'preparar_contexto reconstruye historial, fase Pulse (conversaciones_estado), last_session y señales meta. detectMetaQueryInbound evita reinicios espurios en consultas informativas.'
  },
  {
    title: 'Clasificación Haiku',
    body: 'preparar_estado_intencion arma JSON con lista_pendiente, droga_lista_actual y lista_lineas (opts_cache). clasificar_intencion (Haiku) + parse_intencion_usuario devuelven intent y medication_text; regex sólo como fallback o refuerzo (LAB_FILTER_LIST_V1).'
  },
  {
    title: 'Padrón y enriquecedor',
    body: 'enriquecer_medicamento_cache usa primero medication_text de la IA (iaMedTextEnr), luego API/padrón y filtros por droga + laboratorio (nombrelab). combinar_resolver_sugerencias puede traer padrón por droga cuando el usuario acota solo un lab.'
  },
  {
    title: 'Router conversacional',
    body: 'router_conversacion decide el siguiente mensaje, validación DNI, creación de pedido o derivación a agente humano según paso Redis, intent y contexto clínico.'
  },
  {
    title: 'Respuesta / pedido / humano',
    body: 'Salida por enviar_msg_router, enviar_lista_chatwoot o derivar_agente_humano. Tras create_pedido persiste last_session; meta-consultas y THANKS_ACK pueden cerrar fase en Pulse.'
  }
];

const redisKeys = [
  {
    key: 'fam_state',
    detail: 'Paso conversacional (awaiting_beneficiary, awaiting_dni, awaiting_medication_self, awaiting_tyc, awaiting_recordatorios, last_session…), DNI, vínculo, medicamento_id y medicamento_label.'
  },
  {
    key: 'Confirmación pendiente',
    detail: 'awaiting_med_confirm, pending_medicamento_id, pending_medicamento_label — persistidos con next_state_op = set tras ofrecer «¿Es este?».'
  },
  {
    key: 'bot:hist:{conversationId}',
    detail: 'Cursor de paginación para historial de pedidos aprobados (ASK_PATIENT_HISTORY).'
  },
  {
    key: 'med_cache',
    detail: 'Medicamento activo en formato id|nombre — presentación. Soporta __CLEAR__ al cambiar de droga.'
  },
  {
    key: 'opts_cache',
    detail: 'Listas de presentaciones (dosesList / brandList), drogaNombre de la lista en curso y metadatos nombrelab por ítem — base para filtrar por laboratorio sin perder la droga.'
  },
  {
    key: 'last_session',
    detail: 'Tras create_pedido se guarda last_session; no se borra todo el contexto conversacional.'
  }
];

const haikuRules = [
  'Recibe mensaje + estado JSON de preparar_estado_intencion (incluye droga_lista_actual y lista_lineas cuando hay lista pendiente).',
  'Interpreta frases informales, typos y preguntas coloquiales («tenés algo?», «estoy buscando») sin tomar «algo» o «buscando» como nombre de medicamento.',
  'Devuelve JSON: intent, confidence, medication_text, dni, list_pick_index, reason_short.',
  'Con lista_pendiente y droga_lista_actual: si el usuario nombra solo un laboratorio (Roemmers, Bagó…) → PROVIDE_MEDICATION y medication_text = droga + lab (ej. enalapril roemmers), no CHANGE_MEDICATION ni CLARIFY_UNKNOWN.',
  'parse_intencion_usuario valida la salida; LAB_FILTER_LIST_V1 puede reforzar lab+droga si la IA dudó pero el contexto Redis es claro.',
  'Regla crítica: pregunta ≠ declaración (ej. «¿para quién es?» → aclarar, no FOR_SELF).',
  'Principio operativo: la IA interpreta; enriquecer y router obedecen medication_text cuando intent es PROVIDE_MEDICATION / CHANGE_MEDICATION (iaMedTextEnr).'
];

const enriquecedorPoints = [
  {
    title: 'enriquecer_medicamento_cache',
    body: 'Prioridad de resolución: (1) medication_text de Haiku vía v4IaMedicationText / iaMedTextEnr, (2) alias/regex del mensaje. Evita usar solo «roemmers» o «algo» del texto crudo cuando la IA ya devolvió «enalapril roemmers».'
  },
  {
    title: 'Filtro laboratorio (nombrelab)',
    body: 'Con lista pendiente: poolItemsForDrogaLabFilter acota por drogaMatched / opts.drogaNombre; itemMatchesLabHint filtra por nombrelab del padrón. drogaLockPick evita mezclar otras drogas del mismo lab (COROVAL, POVIRAL, etc.).'
  },
  {
    title: 'combinar_resolver_sugerencias',
    body: 'Fusiona resolver + sugerencias. Si hay lab en mensaje y droga en contexto (medication_text o droga_lista_actual), puede fetchear padrón por droga (LAB_DROGA_PADRON_FETCH) en lugar de buscar solo el token «roemmers».'
  },
  {
    title: 'resolver_medicamento_id',
    body: 'Resuelve URLs con variantes de marca (Lukast compuesto, typos compuesi, Ozempc, dutiden → dulaglutida).'
  },
  {
    title: 'suppress_stale_med',
    body: 'Ignora cache Redis si el usuario nombra otra droga en pasos sensibles: awaiting_dni, awaiting_medication_self, dni_failed.'
  },
  {
    title: 'dosesRebuild + __CLEAR__',
    body: 'Tras cambio de medicamento: __CLEAR__ en med_cache y rebuild de lista de presentaciones si la API devuelve opciones nuevas.'
  },
  {
    title: 'force_router en intents meta',
    body: 'Evita mandar listas silenciosas cuando el usuario pregunta «cómo funciona» o «qué te pedí». Payload incluye nombre comercial y presentación, no sólo ID.'
  }
];

const routerRules = [
  'Decisión central: siguiente mensaje, acción (texto, validar DNI, crear pedido, derivar humano).',
  'Ramas por paso; evita saludo largo durante DNI, TyC o confirmación de lista.',
  'Mantiene medicamento si el usuario repite o pregunta; resetea sólo con cambio explícito.',
  'Cambio titular ↔ familiar con contexto temporal («ese era para mi tía» = pasado, no mantener med).',
  'Prioridad CHANGE_MEDICATION sobre sí/no en recordatorios si el usuario nombra otra droga.',
  'Rama CONFIRM_YES en awaiting_medication_self: usa pending_medicamento_id de Haiku + estado, no el medicamento viejo del cache.',
  'Cambio de droga fresca: userNamedDifferentDrug limpia med_cache/opts_cache antes de resolver.',
  'Emoji / off-topic: shouldDeriveHumanOffContext → derivar_agente_humano + MSG_DERIVAR_AGENTE_HUMANO (estado delete).',
  'Tras ofrecer «¿Es este?», el estado debe guardarse en Redis (if_fam_state_op sólo corre con set, no keep).',
  'persist_fase: finalizada en meta-consultas, THANKS_ACK y acciones patient_history / pending_recipes.'
];

const intentRows = [
  { intent: 'PROVIDE_MEDICATION', covers: 'Nombra medicamento/dosis; lab solo con lista pendiente → medication_text droga+lab (enalapril roemmers)' },
  { intent: 'CHANGE_MEDICATION / NEW_REQUEST_RESET', covers: 'Cambiar de droga o empezar otra gestión' },
  { intent: 'PICK_LIST_OPTION / ASK_MORE_LIST_OPTIONS', covers: 'Elegir de lista (número, ordinal, nombre)' },
  { intent: 'FOR_SELF / FOR_FAMILY / PROVIDE_DNI', covers: 'Beneficiario titular o familiar + validación DNI' },
  { intent: 'CONFIRM_YES / CONFIRM_NO', covers: 'Confirmar presentación, familiar o medicamento ofrecido («sí», «si es ese»)' },
  { intent: 'RECORDATORIOS_YES / NO', covers: 'Preferencia de recordatorios antes del pedido' },
  { intent: 'ASK_SESSION_RECALL', covers: '«¿Qué medicamento te pedí?» / «¿para quién fue?» — resume última gestión' },
  { intent: 'ASK_PATIENT_HISTORY', covers: 'Historial de recetas aprobadas del titular/familiar (paginado, sólo aprobados)' },
  { intent: 'ASK_PENDING_RECIPES', covers: 'Recetas que faltan por emitir (pendiente en trámite o aprobado con recetas_generadas < cantidad_recetas)' },
  { intent: 'ASK_PROGRAM_MEDICATIONS', covers: 'Qué cubre el programa (sin droga concreta)' },
  { intent: 'HOW_DOES_IT_WORK', covers: 'Cómo funciona el trámite por chat' },
  { intent: 'GENERIC_MED_REQUEST', covers: '«Quiero un remedio» sin nombrar fármaco' },
  { intent: 'THANKS_ACK', covers: 'Gracias / cierre amable — respuesta breve, no saludo de bienvenida' },
  { intent: 'ACUTE_SYMPTOM', covers: 'Síntomas agudos — orientación fuera de alcance del canal' },
  { intent: 'OFF_TOPIC', covers: 'Tema ajeno al trámite — el router deriva a operador humano' },
  { intent: 'HUMAN_AGENT', covers: 'Pedir operador, mensaje sólo emoji, o derivación por derive_human_agent' },
  { intent: 'CANCEL_ABORT', covers: 'Abandonar trámite' },
  { intent: 'CLARIFY_UNKNOWN', covers: 'No entendido — pedir aclaración contextual al paso actual' }
];

const manualPages = [
  { id: 'portada', label: 'Portada', eyebrow: 'Manual', title: 'Bot recetas n8n V4' },
  { id: 'arquitectura', label: 'Pipeline', eyebrow: 'Arquitectura', title: 'Flujo end-to-end V4' },
  { id: 'haiku', label: 'Haiku', eyebrow: 'Clasificador', title: 'Clasificador semántico Haiku' },
  { id: 'redis', label: 'Redis', eyebrow: 'Estado', title: 'Persistencia Redis' },
  { id: 'enriquecedor', label: 'Padrón', eyebrow: 'Medicamentos', title: 'Enriquecedor y padrón' },
  { id: 'router', label: 'Router', eyebrow: 'Decisión', title: 'Router conversacional' },
  { id: 'intents', label: 'Intents', eyebrow: 'Semántica', title: 'Intenciones que razona Haiku' },
  { id: 'flujos', label: 'Flujos', eyebrow: 'Casos', title: 'Recetas pendientes y confirmación' },
  { id: 'laboratorio', label: 'Laboratorio', eyebrow: 'Lista + lab', title: 'Filtro por laboratorio' },
  { id: 'handoff', label: 'Handoff', eyebrow: 'Derivación', title: 'Emoji, off-topic y humano' },
  { id: 'fase', label: 'Fase', eyebrow: 'Pulse', title: 'Fase finalizada y sesión pasiva' },
  { id: 'cambio-med', label: 'Cambio med', eyebrow: 'Transición', title: 'Cambio de medicamento y titular' },
  { id: 'integracion', label: 'Integración', eyebrow: 'Operación', title: 'Chatwoot, meta-consultas y cache' }
];

function NoteList({ items, styles }) {
  return (
    <div style={styles.pageStackCompact}>
      {items.map((item) => (
        <div key={item} style={styles.noteRowSoft}>
          <span style={styles.noteAccentSoft} />
          <span style={{ color: '#1e293b', lineHeight: 1.65, fontSize: 14 }}>{item}</span>
        </div>
      ))}
    </div>
  );
}

function IntentTable({ styles, compact }) {
  return (
    <div style={{ display: 'grid', gap: compact ? 6 : 8 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: compact ? '1fr' : 'minmax(140px, 0.42fr) 1fr',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 12,
        background: 'linear-gradient(135deg, #163d6b 0%, #2365ab 100%)',
        color: '#f8fbff',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.06em',
        textTransform: 'uppercase'
      }}>
        {!compact && <span>Intent</span>}
        <span>Qué cubre</span>
      </div>
      {intentRows.map((row) => (
        <article
          key={row.intent}
          style={{
            ...styles.infoCard,
            padding: compact ? 10 : 12,
            display: 'grid',
            gridTemplateColumns: compact ? '1fr' : 'minmax(140px, 0.42fr) 1fr',
            gap: compact ? 4 : 10,
            alignItems: 'start'
          }}
        >
          <div style={{
            fontSize: compact ? 10 : 11,
            fontWeight: 800,
            color: '#163d6b',
            letterSpacing: '0.02em',
            lineHeight: 1.35
          }}>
            {row.intent}
          </div>
          <div style={{ fontSize: compact ? 12 : 13, color: '#334155', lineHeight: 1.55 }}>
            {row.covers}
          </div>
        </article>
      ))}
    </div>
  );
}

function renderManualPage(pageId, styles, { isMobile, forExport, manualPages: pages }) {
  const compactCardStyle = {
    ...styles.infoCard,
    padding: forExport ? 14 : styles.infoCard.padding,
    boxShadow: forExport ? 'none' : styles.infoCard.boxShadow
  };

  switch (pageId) {
    case 'portada':
      if (forExport) {
        return (
          <div style={styles.exportCoverShell}>
            <div style={styles.exportCoverOverline}>Documentación técnica interna</div>
            <div style={styles.exportCoverBrand}>n8n · V4</div>
            <h1 style={styles.exportCoverTitle}>Manual bot recetas n8n V4</h1>
            <p style={styles.exportCoverText}>
              Arquitectura IA-first, clasificador Haiku, medication_text, filtro por laboratorio, Redis, router e intents del workflow V4 de recetas crónicas.
            </p>
            <div style={styles.exportCoverDivider} />
            <div style={styles.exportCoverFooter}>
              <span style={styles.exportCoverFooterItem}>Workflow {WORKFLOW_ID.slice(0, 8)}…</span>
              <span style={styles.exportCoverFooterItem}>Sólo V4</span>
              <span style={styles.exportCoverFooterItem}>Admin only</span>
            </div>
          </div>
        );
      }

      return (
        <div style={styles.coverPageShell}>
          <div style={styles.coverPageGlow} />
          <div style={styles.coverPageTopline}>Documentación técnica interna</div>
          <div style={styles.coverPageBrand}>n8n · V4 bot recetas</div>
          <h1 style={styles.coverPageTitle}>Manual bot recetas n8n V4</h1>
          <p style={styles.coverPageText}>
            Arquitectura IA-first: Haiku interpreta con contexto de lista; enriquecer obedece medication_text; filtros de laboratorio sobre la droga en curso — sin referencias a V1/V2/V3.
          </p>
          <div style={styles.coverPageEditionRow}>
            <span style={styles.coverPageEditionChip}>Junio 2026</span>
            <span style={styles.coverPageEditionChip}>{pages.length} capítulos</span>
            <span style={styles.coverPageEditionChip}>Workflow V4</span>
          </div>
          <div style={styles.coverPageDivider} />
          <div style={styles.coverPageMetaGrid}>
            <div style={styles.coverPageMetaCard}>
              <div style={styles.coverPageMetaLabel}>Workflow</div>
              <div style={{...styles.coverPageMetaValue, fontSize: '13px'}}>{WORKFLOW_ID}</div>
            </div>
            <div style={styles.coverPageMetaCard}>
              <div style={styles.coverPageMetaLabel}>Host</div>
              <div style={{...styles.coverPageMetaValue, fontSize: '13px'}}>{WORKFLOW_HOST}</div>
            </div>
            <div style={styles.coverPageMetaCard}>
              <div style={styles.coverPageMetaLabel}>Alcance</div>
              <div style={{...styles.coverPageMetaValue, fontSize: '13px'}}>Recetas crónicas</div>
            </div>
          </div>
        </div>
      );

    case 'arquitectura':
      return (
        <div style={styles.pageStack}>
          <div style={styles.coverLeadCard}>
            <div style={styles.coverLeadLabel}>Workflow v4 bot recetas</div>
            <p style={{ ...styles.coverLeadText, marginBottom: 0 }}>
              Entrada Chatwoot/Webhook → contexto cliente → clasificación → padrón → router → respuesta / pedido / agente humano.
            </p>
          </div>
          <div style={styles.pageStackCompact}>
            {pipelineSteps.map((step, index) => (
              <article key={step.title} style={styles.flowCard}>
                <div style={styles.flowIndex}>{index + 1}</div>
                <div>
                  <h3 style={styles.cardTitle}>{step.title}</h3>
                  <p style={{ ...styles.copy, marginBottom: 0, fontSize: isMobile ? 13 : 14 }}>{step.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      );

    case 'haiku':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <div style={styles.cardTag}>Nodo clasificar_intencion</div>
            <p style={styles.copy}>
              Modelo Haiku recibe el mensaje junto con estado JSON (lista_pendiente, droga_lista_actual, lista_lineas).
              Es el primer filtro semántico; parse_intencion_usuario valida y puede reforzar lab+droga. El enriquecedor
              debe usar medication_text cuando la IA clasifica PROVIDE_MEDICATION con confianza.
            </p>
          </article>
          <NoteList items={haikuRules} styles={styles} />
          <article style={styles.highlightPanel}>
            <div style={styles.highlightTitle}>Salida JSON esperada</div>
            <p style={{ ...styles.highlightPanelText, marginBottom: 0, fontFamily: 'Consolas, monospace', fontSize: 13 }}>
              intent · confidence · medication_text · dni · list_pick_index · reason_short
            </p>
          </article>
        </div>
      );

    case 'redis':
      return (
        <div style={styles.pageStackCompact}>
          {redisKeys.map((item) => (
            <article key={item.key} style={compactCardStyle}>
              <div style={styles.cardTag}>{item.key}</div>
              <p style={{ ...styles.copy, marginBottom: 0 }}>{item.detail}</p>
            </article>
          ))}
        </div>
      );

    case 'enriquecedor':
      return (
        <div style={styles.pageStackCompact}>
          {enriquecedorPoints.map((point) => (
            <article key={point.title} style={compactCardStyle}>
              <h3 style={styles.cardTitle}>{point.title}</h3>
              <p style={{ ...styles.copy, marginBottom: 0 }}>{point.body}</p>
            </article>
          ))}
        </div>
      );

    case 'router':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <div style={styles.cardTag}>router_conversacion</div>
            <p style={{ ...styles.copy, marginBottom: 0 }}>
              Nodo de decisión central del V4. Combina paso Redis (fam_state), intent Haiku, caches de medicamento
              y reglas de negocio para elegir texto, validación DNI, create_pedido o derivación humana.
            </p>
          </article>
          <NoteList items={routerRules} styles={styles} />
          <article style={styles.highlightPanelAlt}>
            <div style={styles.highlightTitle}>Persistencia de fase</div>
            <p style={{ ...styles.copy, marginBottom: 0, color: '#1d3d67' }}>
              Cadena if_router_persist_meta_fase → persistir_estado_meta_finalizada escribe fase finalizada en Pulse
              tras meta-consultas, THANKS_ACK, patient_history y pending_recipes.
            </p>
          </article>
        </div>
      );

    case 'intents':
      return <IntentTable styles={styles} compact={forExport} />;

    case 'laboratorio':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Flujo típico: enalapril → Roemmers</h3>
            <ol style={styles.list}>
              <li>Usuario: «enalapril quiero» → lista de presentaciones (opts_cache, drogaNombre: enalapril).</li>
              <li>Usuario: «No tenés de Roemmers?» / «roemmers estoy buscando tenés algo?» → Haiku: PROVIDE_MEDICATION, medication_text: enalapril roemmers.</li>
              <li>enriquecer usa iaMedTextEnr; filtra pool por droga + nombrelab; muestra LOTRIAL / ECAPRILAT, no todo el catálogo Roemmers.</li>
            </ol>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Errores que se evitaron (jun 2026)</h3>
            <ul style={styles.list}>
              <li>Resolver solo «roemmers» → POVIRAL, CARDIAX, COROVAL (otras drogas del lab).</li>
              <li>Regex del mensaje tomando «buscando» o «algo» como nombre comercial.</li>
              <li>opts_cache con drogaNombre incorrecta tras pisar contexto (amiodarona / lista basura).</li>
              <li>IA correcta pero enriquecer ignorando medication_text — corregido con iaMedTextEnr.</li>
            </ul>
          </article>
          <article style={styles.highlightPanel}>
            <div style={styles.highlightTitle}>Cadena de nodos</div>
            <p style={{ ...styles.highlightPanelText, marginBottom: 0, fontSize: 13, lineHeight: 1.6 }}>
              preparar_estado_intencion → clasificar_intencion → parse_intencion_usuario → preparar_query_sugerencias /
              combinar_resolver_sugerencias → enriquecer_medicamento_cache → router_conversacion → enviar_lista_chatwoot
            </p>
          </article>
          <article style={styles.highlightPanelAlt}>
            <div style={styles.highlightTitle}>Fuente de verdad del prompt IA</div>
            <p style={{ ...styles.copy, marginBottom: 0, color: '#1d3d67' }}>
              scripts/lib/intent-v4-layer.js (INTENT_SYSTEM_MESSAGE, preparar estado). Parches de despliegue:
              patch-n8n-v4-enriquecer-honor-ia-medtext.js, patch-n8n-v4-lab-nombrelab-context.js,
              patch-n8n-v4-fix-lab-filter-pending-pool.js, patch-n8n-v4-fix-roemmers-lab-padron-fetch.js.
            </p>
          </article>
        </div>
      );

    case 'flujos':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Recetas pendientes por emitir</h3>
            <p style={styles.copy}>
              Intent ASK_PENDING_RECIPES — Haiku interpreta; acción pending_recipes. Mismo endpoint GET …/pedidos
              vía http_pedidos_historial → preparar_historial_pedidos (modo pending).
            </p>
            <ul style={styles.list}>
              <li>Filtro por DNI (titular o familiar si el mensaje lo indica).</li>
              <li>Incluye estados pendiente_* y aprobado con recetas_generadas &lt; cantidad_recetas.</li>
              <li>Campo calculado recetas_restantes_por_emitir en el JSON para Haiku.</li>
            </ul>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Flujo «receta para mí» y confirmación</h3>
            <ul style={styles.list}>
              <li>Usuario elige gestión para sí → paso awaiting_medication_self.</li>
              <li>Typo «lukaste compuesi tenes?» → resolver busca Lukast Compuesto; enriquecedor suprime cache stale.</li>
              <li>Router ofrece presentación: «¿Es este?» y guarda pending_medicamento_id (next_state_op = set).</li>
              <li>CONFIRM_YES → registra medicamento y pasa a recordatorios o TyC.</li>
              <li>Contexto Haiku: awaiting_confirmacion_medicamento y medicamento_pendiente_confirmar.</li>
              <li>Si CONFIRM_YES llega sin pending guardado, el bot pide repetir el paso.</li>
            </ul>
          </article>
        </div>
      );

    case 'handoff':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Emoji y mensajes sin texto útil</h3>
            <p style={{ ...styles.copy, marginBottom: 0 }}>
              detectInboundEmojiOnly en parse/router → intent HUMAN_AGENT. El bot no intenta continuar el flujo clínico
              con entradas vacías de contenido semántico.
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Off-topic y derivación humana</h3>
            <ul style={styles.list}>
              <li>Fuera de contexto (clima, deportes, chistes): Haiku puede marcar OFF_TOPIC.</li>
              <li>El enriquecedor activa derive_human_agent; router usa shouldDeriveHumanOffContext.</li>
              <li>No responde con MSG_OFF_TOPIC automático — deriva a operador humano.</li>
              <li>Parche: patch-n8n-v4-emoji-offtopic-human-handoff.js</li>
            </ul>
          </article>
          <article style={styles.highlightPanel}>
            <div style={styles.highlightTitle}>Estado tras derivación</div>
            <p style={{ ...styles.highlightPanelText, marginBottom: 0 }}>
              derivar_agente_humano + MSG_DERIVAR_AGENTE_HUMANO con estado delete. El bot deja de responder
              mientras CRM/Pulse gestiona la conversación (silence-bot-during-crm-handoff).
            </p>
          </article>
        </div>
      );

    case 'fase':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Fase finalizada (Pulse / CRM)</h3>
            <p style={styles.copy}>
              Tabla conversaciones_estado.fase: tras pedido o consulta informativa, la fase queda finalizada
              (no vuelve a inicio ni muestra «Tomar conversación» indebidamente).
            </p>
            <ul style={styles.list}>
              <li>Meta-consultas → detectMetaQueryInbound en preparar_contexto; no activan hasRestartIntent.</li>
              <li>THANKS_ACK y typos (graias) → respuesta breve con last_session si aplica.</li>
              <li>Router expone persist_fase; cadena if_router_persist_meta_fase → persistir_estado_meta_finalizada.</li>
            </ul>
          </article>
          <article style={styles.highlightPanelAlt}>
            <div style={styles.highlightTitle}>Parches relacionados</div>
            <ul style={{ ...styles.list, color: '#1d3d67', marginBottom: 0 }}>
              <li>patch-n8n-v4-meta-query-fase-finalizada.js</li>
              <li>patch-n8n-v4-thanks-fase-finalizada.js</li>
              <li>patch-n8n-v4-fix-parse-thanks-norm.js</li>
            </ul>
          </article>
        </div>
      );

    case 'cambio-med':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Cambio de medicamento + titular</h3>
            <p style={styles.copy}>
              «bueno mejor quiero otro medicamento para mi» → CHANGE_MEDICATION (parse override + Haiku).
              Respuesta contextual menciona medicamento anterior, cambio a titular y pide nueva droga + dosis.
            </p>
            <ul style={styles.list}>
              <li>Estado awaiting_medication_self con medicamento_id: null, dni_failed: false.</li>
              <li>med_cache: __CLEAR__ al cambiar de droga.</li>
              <li>«levotiroxina quiero»: enriquecedor suprime cache stale, reconstruye EUTHYROX.</li>
              <li>medMatchesUserDrug rechaza DUTIDE si el usuario dice levotiroxina.</li>
              <li>Cambio med + para mí: mensaje contextual con medicamento anterior.</li>
            </ul>
          </article>
          <article style={styles.highlightPanelAlt}>
            <div style={styles.highlightTitle}>Parches clave</div>
            <ul style={{ ...styles.list, color: '#1d3d67', marginBottom: 0 }}>
              <li>patch-n8n-v4-change-med-contextual-ack.js</li>
              <li>patch-n8n-v4-fix-stale-med-after-change-self.js</li>
              <li>patch-n8n-v4-pending-self-med-after-family.js</li>
              <li>patch-n8n-v4-fix-fresh-drug-overrides-cache.js</li>
            </ul>
          </article>
        </div>
      );

    case 'integracion':
      return (
        <div style={styles.pageStack}>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Integración Chatwoot</h3>
            <p style={styles.copy}>
              Tokens de API sincronizados en enviar_lista_chatwoot, enviar_msg_router, enviar_respuesta_chatwoot
              y derivados. Evita Invalid Access Token tras rotación (pedido creado pero sin mensaje en WhatsApp).
            </p>
            <p style={{ ...styles.copy, marginBottom: 0 }}>
              Parche: patch-n8n-v4-sync-chatwoot-token.js
            </p>
          </article>
          <article style={compactCardStyle}>
            <h3 style={styles.cardTitle}>Meta-consultas y cache</h3>
            <ul style={styles.list}>
              <li>«¿Qué medicamentos tengo aprobados?» → ASK_PATIENT_HISTORY / historial, no búsqueda «aprobados».</li>
              <li>«¿Qué recetas me faltan emitir?» → ASK_PENDING_RECIPES antes de wantsNewPedidoAfterLast.</li>
              <li>Guard META_QUERY_NO_NEW_PEDIDO evita pedido espurio tras last_session.</li>
              <li>Typos dutiden / dutide → dulaglutida y reconocimiento crónico DUTIDE.</li>
              <li>Nueva droga tras «cambiar medicamento» limpia Redis stale.</li>
            </ul>
          </article>
          <div style={styles.coverLeadCard}>
            <div style={styles.coverLeadLabel}>Estabilización jun 2026</div>
            <p style={{ ...styles.coverLeadText, marginBottom: 0 }}>
              Capa IA→enriquecer (iaMedTextEnr), filtro laboratorio sobre droga en lista, fetch de padrón por droga en combinar.
              TDZ router/enriquecer (intentV4 temprano). Helpers isLabHintToken definidos antes de uso en enriquecer.
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
}

export default function AdminManualN8n() {
  return (
    <ManualBookShell
      manualPages={manualPages}
      renderManualPage={renderManualPage}
      pdfFileName="manual-n8n-v4-bot-recetas-a4.pdf"
      pdfPreviewTitle="Manual n8n V4 - PDF A4"
    />
  );
}
